import { useTranslation } from "react-i18next"

import type { SessionLocal } from "@/services/dexie"

import { InstructionImage } from "../../components/icons/InstructionImage"
import { ExtraIcons } from "../../components/icons/InstructionImageType"
import { isSupportedVehicleType } from "../../locales/photoType"
import { isExpectedResultsEnabled } from "./introUtils"

export const introSubStepIds = [
  "welcome",
  "requirements",
  "getting-started",
  "watch-out-for-cars",
  "make-room",
  "permissions",
  "gps",
] as const

export type IntroSubStepId = (typeof introSubStepIds)[number]

export type IntroSubStep = {
  id: IntroSubStepId
  icon?: React.ReactElement<{ className?: string }>
  title?: string
  text?: string
  /** If set, renders IntroRequirements instead of IntroContent */
  isRequirements?: true
  /** If set, renders IntroExpectedResults instead of IntroContent */
  isExpectedResults?: true
}

// Substeps in Intro ordered step (/intro/welcome, /intro/requirements ...)
export function useIntroSubSteps(detail: SessionLocal | undefined) {
  const { t } = useTranslation()

  if (!detail) return []

  const { introDisabled, requirementsIntroScreenEnabled } = detail
  const renderExpectedInfoScreen = isExpectedResultsEnabled(detail)
  const vt = isSupportedVehicleType(detail.vehicleType) ? detail.vehicleType : "PersonalVehicle"

  const introSubSteps: IntroSubStep[] = introDisabled
    ? []
    : [
        {
          id: "getting-started",
          icon: <InstructionImage iconName={ExtraIcons.PhotoAndUnderwriting} animated={true} />,
          title: t(`intro.welcome.${vt}.title`),
          text: t(`intro.welcome.${vt}.text`),
        },
        {
          id: "watch-out-for-cars",
          icon: <InstructionImage iconName={ExtraIcons.WatchOutOfCars} animated={true} />,
          title: t(`intro.carsaround.${vt}.title`),
          text: t(`intro.carsaround.${vt}.text`),
        },
        {
          id: "make-room",
          icon: <InstructionImage iconName={ExtraIcons.MakeRoom} animated={true} />,
          title: t("intro.distance.title"),
          text: t(`intro.distance.${vt}.text`),
        },
        {
          id: "permissions",
          icon: <InstructionImage iconName={ExtraIcons.PermissionsLocation} animated={true} />,
          title: t("intro.camera.title"),
          text: t(`intro.camera.${vt}.text`),
        },
        {
          id: "gps",
          icon: <InstructionImage iconName={ExtraIcons.PermissionsGps} animated={true} />,
          title: t("intro.gps.title"),
          text: t("intro.gps.text"),
        },
      ]

  const result: IntroSubStep[] = [...introSubSteps]

  if (requirementsIntroScreenEnabled) {
    result.unshift({ id: "requirements", isRequirements: true })
  }

  if (renderExpectedInfoScreen) {
    result.unshift({ id: "welcome", isExpectedResults: true })
  }

  return result
}
